import { InjectionToken, Signal } from '@angular/core';
import {
  ActionCard,
  CardIntent,
  CardPriority,
  CardSource,
  CockpitFilters,
  CockpitMember,
  CockpitTask,
  CockpitTaskOutcome,
  ContactRole
} from '@shared/definitions/action-card.model';
import { CockpitGroup } from '@app/pages/apps/cockpit/services/cockpit-group.util';

export interface ApproveResult {
  status: 'executed' | 'failed' | 'pending' | 'waiting_external' | 'stale';
  executedEffects: string[];
  failedEffects: { effectId: string; message?: string }[];
  workflowStatus?: {
    nextStepIndex: number;
    workflowCompleted: boolean;
    waitingFor?: { kind: string; hint: string };
  };
  /**
   * Present only when `status === 'stale'`: the card's action no longer
   * matches current domain state (rent already paid, indexation already
   * applied, lease renewed). Nothing ran — the caller warns the owner, who
   * dismisses the card or re-approves with `force: true`.
   */
  staleReason?: { code: string; detail?: string };
}

/** `propor` (default) = SendGrid dispatches. `manual` = owner is sending
 * the mail themselves; backend stamps the draft as `sent_external`. */
export type ApproveSendMode = 'propor' | 'manual';

/**
 * The three actions a selection of cards can take at once. Approving is
 * deliberately absent: it executes effects (real mail, writes outside the
 * cockpit) with no undo, which is not something fifty cards should do on one
 * click. Mirrors `BulkCardActionType` in the backend callable.
 */
export type BulkCardAction = 'seen' | 'snooze' | 'reject';

/** Max cards per bulk call — mirrors `MAX_BULK_CARD_IDS` in the backend. */
export const MAX_BULK_CARDS = 50;

/**
 * How many terminal cards `loadArchive()` pulls in. It lives on the CONTRACT,
 * not inside the Firestore store, because the feed now names this bound to the
 * owner (#257) — the store enforces it, the flux quotes it, and a copy of the
 * number in a translated string would go stale the day it changes.
 */
export const ARCHIVE_PAGE_SIZE = 50;

/**
 * Rolling 30-day cockpit figures (#256) — mirrors `CockpitRolling30` in
 * `backend/functions/src/cockpit/computeCockpitStats.ts`, the only writer.
 * Read-only for the client; the rules forbid writing it.
 */
export interface CockpitRolling30 {
  windowDays: number;
  /** Resolved cards behind the figures — shown so the reader can judge them. */
  sampleSize: number;
  avgHandlingMs: number;
  medianHandlingMs: number;
  /** 0..1. */
  automatedShare: number;
  computedAt: string;
  version: string;
}

/**
 * Per-card outcome of a bulk run. `already-resolved` is separated from a
 * generic error because the UI names it back: "2 ignorées : déjà résolues"
 * tells the owner nothing is broken, "2 échecs" does not.
 */
export interface BulkCardFailure {
  cardId: string;
  code: 'not-found' | 'already-resolved' | 'error';
  message: string;
}

export interface BulkCardResult {
  ok: string[];
  failed: BulkCardFailure[];
}

export interface ApproveOptions {
  sendMode?: ApproveSendMode;
  /**
   * Owner-picked branch target for a `choice` step (one of the active
   * step's `branchChoices[].nextStepId` / `allowedTransitions`). Forwarded
   * to the backend's `advanceWorkflow`; ignored for linear/single-step cards.
   */
  chosenNextStepId?: string;
  /**
   * Bypass the backend relevance pre-check. Set after the owner explicitly
   * chooses "send anyway" on the stale-card warning, so a previously-stale
   * card executes on the second call.
   */
  force?: boolean;
}

/**
 * Contract implemented by `CockpitFirestoreStore` (real-time Firestore
 * backend). The Cockpit UI injects `COCKPIT_STORE` instead of a concrete
 * class, so components stay decoupled from the store implementation.
 */
export interface ICockpitStore {
  readonly cards: Signal<readonly ActionCard[]>;
  readonly selectedId: Signal<string | null>;
  readonly filters: Signal<CockpitFilters>;

  /**
   * Members of the active workspace, for the assignment picker and the
   * assignee filter. Exposed here so presentation components never inject
   * `WorkspaceStore` themselves — and so the demo store can offer a plausible
   * team without a Firestore round-trip. Empty until the workspace resolves.
   */
  readonly workspaceMembers: Signal<readonly CockpitMember[]>;
  /**
   * Uid of the signed-in user, or null before auth resolves. Backs the
   * "my cards" filter and the "this one is yours" ring on the feed badge.
   */
  readonly currentUserId: Signal<string | null>;

  readonly filteredCards: Signal<readonly ActionCard[]>;
  readonly selectedCard: Signal<ActionCard | null>;
  readonly counts: Signal<{
    pending: number;
    executed: number;
    approved: number;
    rejected: number;
    snoozed: number;
    failed: number;
    handledExternally: number;
    superseded: number;
    total: number;
  }>;

  /**
   * All-time count of terminal (archived) cards. Backs the dashboard's
   * "Archivées" tab badge + "N de plus" without loading the whole archive into
   * memory — sourced from a `count()` aggregate when reads are bounded, or the
   * loaded card set otherwise.
   */
  readonly archivedTotal: Signal<number>;

  /**
   * Whether the on-demand archive has been pulled in this session (#257).
   * Drives the "search the archive too" affordance: an offer to load something
   * already loaded is noise, and a search that silently misses the archive
   * without saying so is worse. The demo store answers `true` — its whole seed
   * lives in memory.
   */
  readonly archiveLoaded: Signal<boolean>;

  readonly pendingCards: Signal<readonly ActionCard[]>;
  /**
   * Cards in `scheduled_auto` status — auto-execute timer running.
   * Sorted by `resolution.autoExecuteAt` so the soonest-firing comes
   * first. Used by the dashboard's "Scheduled" section + counts.
   */
  readonly scheduledCards: Signal<readonly ActionCard[]>;
  /**
   * Cards in `waiting_external` status — parked on a WAIT_INBOUND step
   * (e.g. awaiting a dispatched provider's reply). Newest first. Used by
   * the dashboard's "Réponses en attente" section.
   */
  readonly waitingCards: Signal<readonly ActionCard[]>;
  /**
   * Cards the owner has snoozed (`status === 'snoozed'`). Sorted by
   * `resolution.snoozeUntil` ascending so the soonest-waking card comes
   * first (cards without a wake date sort last). Powers the dashboard's
   * bottom "En attente / en veille" zone — without it these cards vanish
   * from the cockpit entirely.
   */
  readonly snoozedCards: Signal<readonly ActionCard[]>;
  /**
   * Terminally-resolved cards — `executed` / `approved` / `rejected` /
   * `handled_externally` / `superseded`, plus `failed` (kept here so a
   * failed action never silently disappears; the detail drawer still
   * offers retry). Sorted by `resolution.resolvedAt` descending (most
   * recent first). Powers the dashboard's "Archivées" tab.
   */
  readonly archivedCards: Signal<readonly ActionCard[]>;
  /**
   * Cards with an in-flight multi-step workflow (`workflow.status ===
   * 'active'`), whether mid-step or parked. Powers the global "active
   * workflows" indicator (nav badge, dashboard tile).
   */
  readonly activeWorkflowCards: Signal<readonly ActionCard[]>;
  readonly urgentCards: Signal<readonly ActionCard[]>;
  readonly cardsByCategory: Signal<{
    money: readonly ActionCard[];
    repairs: readonly ActionCard[];
    contracts: readonly ActionCard[];
    communications: readonly ActionCard[];
  }>;
  /**
   * Long-lived in-progress cards bucketed by business type with per-status
   * sub-counts. Powers the dashboard's "En cours" tiles. Only non-empty
   * groups, in canonical tile order. See `cockpit-group.util.ts`.
   */
  readonly inProgressGroups: Signal<readonly CockpitGroup[]>;

  /**
   * Rolling 30-day figures written nightly by the backend (#256), or `null`
   * when the workspace has not resolved enough cards for an honest average.
   *
   * It sits on the CONTRACT rather than in a Firestore-reading service of its
   * own for two reasons: the KPI strip injects nothing but this store, and the
   * demo store must be able to serve a plausible figure so the strip is
   * recettable without a deployed backend. `null` means "say nothing" — never
   * a zero.
   */
  readonly rolling30: Signal<CockpitRolling30 | null>;
  readonly financialSummary: Signal<{
    overdueAmount: number;
    upcomingAmount: number;
    overdueCount: number;
    upcomingCount: number;
    currency: string;
  }>;
  readonly resolvedToday: Signal<readonly ActionCard[]>;
  readonly resolvedTodayBreakdown: Signal<{
    total: number;
    executed: number;
    rejected: number;
    snoozed: number;
  }>;

  /**
   * Owner-side tasks written by the `CREATE_TASK` executor — calendar
   * appointments (syndic visits, meter transfers, walkthroughs),
   * follow-up reminders, etc. Streams `workspaces/{wid}/cockpitTasks`
   * in realtime. Empty when the workspace has no scenarios that emit
   * tasks yet.
   */
  readonly cockpitTasks: Signal<readonly CockpitTask[]>;
  /**
   * Convenience view: `cockpitTasks` filtered to `status === 'open'`
   * and sorted by `dueDate` ascending (nulls last). Rendered by
   * `CockpitTasksComponent` — the cockpit's "Suivis" pane and the
   * `/cockpit/tasks` route (#353).
   */
  readonly openTasks: Signal<readonly CockpitTask[]>;
  /**
   * True until the first `cockpitTasks` snapshot lands, and again on every
   * workspace switch. Without it an empty list is indistinguishable from a
   * pending one, and the surface would flash "rien à suivre" at somebody who
   * has ten open follow-ups (DESIGN.md §13.3 — toute attente a un visage).
   */
  readonly tasksLoading: Signal<boolean>;

  /**
   * Take a follow-up off the open list (#374). `done` = it happened,
   * `cancelled` = it will not — the two are distinct statements, see
   * {@link CockpitTaskOutcome}.
   *
   * Writes `status` + `closedAt` + `closedBy` straight to the document; those
   * three fields are the whole of what `firestore.rules` lets a member change
   * on a `cockpitTasks` doc. Rejects (permission denied, offline) propagate —
   * the caller must surface them, because the row is already gone from the
   * local snapshot when the promise settles and coming back silently would
   * read as a glitch.
   */
  closeTask(taskId: string, outcome: CockpitTaskOutcome): Promise<void>;

  /**
   * Undo a {@link closeTask} — back to `open`, signature cleared. Exists so the
   * close does not have to be guarded by a confirmation dialog: a mis-click is
   * repaired from the toast, which is cheaper than asking everybody to confirm
   * every time (DESIGN.md §13 — le produit ne punit pas).
   */
  reopenTask(taskId: string): Promise<void>;

  /**
   * Lazily load the archive of terminal cards for display (the dashboard's
   * "Archivées" tab / the inbox terminal views). No-op once loaded or when
   * bounded reads are disabled (the full list is already present).
   */
  loadArchive(): void | Promise<void>;

  select(id: string): void;
  clearSelection(): void;
  /**
   * The id of the next still-actionable card after `cardId` in the active feed
   * order, or null when there is none — used to auto-advance the detail pane to
   * the next card after the owner resolves one (instead of stranding on the
   * now-"terminée" card). `cardId` is the reference point and may still be in
   * the list at call time (capture it BEFORE the resolving mutation for the most
   * accurate "next in position"); falls back to the first remaining card.
   */
  nextPendingAfter(cardId: string): string | null;
  updateFilters(patch: Partial<CockpitFilters>): void;
  resetFilters(): void;
  toggleSource(source: CardSource): void;
  toggleIntent(intent: CardIntent): void;
  togglePriority(priority: CardPriority): void;

  /** Toggle a single effect's `enabled` flag within a card's bundle. */
  toggleEffect(cardId: string, effectId: string): void | Promise<void>;
  /**
   * Execute the card with the currently-enabled effects. If `draftEdits` is
   * provided, applies edits to specific effects' draftBody before executing.
   * If `payloadEdits` is provided, shallow-merges per-effect payload
   * overrides (e.g. CREATE_REPAIR title/description/priority) before
   * dispatch — used for workflow-step inline editing.
   */
  approveWithEffects(
    cardId: string,
    draftEdits?: Record<string, string>,
    payloadEdits?: Record<string, Record<string, unknown>>,
    options?: ApproveOptions
  ): void | Promise<ApproveResult | void>;

  /**
   * Multi-step workflow only: skip the active step without executing any
   * of its effects. Backend marks the step `skipped` and advances to the
   * next one (same code path as approve, just with an empty
   * `enabledEffectIds` set). On a single-step card this is a no-op.
   */
  skipStep(cardId: string): void | Promise<ApproveResult | void>;

  /** Convert a card surfaced as handled-externally back to pending. */
  claimAsPending(cardId: string): void | Promise<void>;

  approve(id: string): void | Promise<void>;
  /**
   * Universal baseline: mark the card seen/read (stamps `seenAt` + a `VIEWED`
   * event via the `markCardSeen` callable). The card stays `pending` (NOT
   * archived) but drops out of the "needs you now" lane unless high-stakes.
   */
  markSeen(id: string): void | Promise<void>;

  /**
   * Make a workspace member responsible for the card, or clear the assignment
   * with `userId: null`. Goes through the `assignCard` callable — the Firestore
   * rules keep client writes to `status`/`resolution`/`updatedAt`, so this can
   * never be a direct `updateDoc`. Each gesture appends an `ASSIGNED` event;
   * re-assigning to the same member is a no-op server-side.
   */
  assign(cardId: string, userId: string | null): void | Promise<void>;
  editAndApprove(id: string, patch: { draftBody?: string }): void | Promise<void>;
  reject(id: string, reason: string): void | Promise<void>;
  snooze(id: string, untilIso: string): void | Promise<void>;
  reopen(id: string): void | Promise<void>;

  /**
   * Apply ONE low-stakes action to a selection of cards in a single round-trip
   * (`bulkCardAction` callable). At most {@link MAX_BULK_CARDS} ids; beyond
   * that the backend refuses outright rather than truncating, so the caller
   * must gate the selection itself.
   *
   * Resolves with a PARTIAL result: cards that could not be actioned (deleted,
   * already resolved) come back in `failed` while the rest went through — a
   * bulk run never fails as a whole just because one card did.
   */
  bulkAction(
    cardIds: string[],
    action: BulkCardAction,
    payload?: { snoozeUntil?: string; reason?: string }
  ): Promise<BulkCardResult>;

  /**
   * Owner-driven "Mettre en attente": park the card in `waiting_external`
   * (it moves to the dashboard's bottom "En attente" zone) without resolving
   * it. Un-park via `reopen`. Distinct from `snooze` (which has a wake date)
   * and from the backend's automatic park (which stamps `card.wait` for
   * inbound/timeout auto-resolution).
   */
  park(id: string): void | Promise<void>;

  /**
   * Terminal-resolve a card as `superseded` because its action is no longer
   * relevant (rent already paid, indexation applied, lease renewed). Driven
   * by the "dismiss" choice on the stale-card warning. `reason` is the
   * relevance reason code for the audit trail.
   */
  resolveNoLongerRelevant(id: string, reason: string): void | Promise<void>;

  /**
   * Terminal-resolve a card as `handled_externally` — the owner did the
   * work outside the cockpit (phone call, in-person, manual email). Used
   * as the secondary recovery path on a `failed` workflow card when
   * retrying the step doesn't make sense.
   */
  markHandledExternally(id: string, note?: string): void | Promise<void>;

  /**
   * Owner-correction of the card's resolved contact / property /
   * contract. Fields with `undefined` stay unchanged, `null` clears,
   * a string sets the new id (server stamps `owner_manual` source).
   * `tenantIds` is the owner's pick of tenants, in order (`null`/`[]` =
   * back to the lease's); the first becomes the addressee of tenant mails.
   * `opts.refreshRecipient` asks the server to re-read the already-linked
   * lease's tenant and stamp it on the card (automatic attach, empty patch).
   */
  updateLinkage(
    cardId: string,
    patch: { contactId?: string | null; propertyId?: string | null; contractId?: string | null; tenantIds?: string[] | null },
    opts?: { refreshRecipient?: boolean }
  ): void | Promise<void>;

  /**
   * V2.6 — regenerate a mail effect's draft. Without `feedback` the backend
   * re-grounds deterministically (0 AI cost); with `feedback` it revises via
   * the `cockpitDraftRegenerate` AI task. The realtime stream delivers the
   * new `draftBody`. Returns whether the body actually changed.
   */
  regenerateDraft(
    cardId: string,
    effectId: string,
    feedback?: string
  ): Promise<{ draftBody: string; changed: boolean }>;

  /**
   * Write (or serve from cache) the progress band's AI narrative — « où en est
   * ce dossier ? ». On demand only: nothing calls this but the band's button.
   *
   * `lang` is the reader's current UI language; the backend falls back to the
   * workspace locale. `force` regenerates a summary the backend still considers
   * fresh — what the « Régénérer » affordance sends once the cached paragraph
   * has gone stale. The realtime stream delivers `progressSummary`, so the
   * returned text is for the in-flight render only, not for storing locally.
   */
  summarizeProgress(
    cardId: string,
    lang: string,
    force?: boolean
  ): Promise<{ text: string; cached: boolean }>;

  /**
   * Promote a discovered cockpit contact to a formal `tenants/{id}` or
   * `service_providers/{id}` record so future inbound emails resolve
   * via the high-confidence cockpit-index path.
   */
  promoteContact(
    contactId: string,
    target: { tenantId?: string; serviceProviderId?: string }
  ): void | Promise<void>;

  /**
   * Update a cockpit contact's persona role. Used by the inline role
   * picker on the card header when `senderRole === 'unknown'`.
   */
  updateContactRole(contactId: string, role: ActionCard['senderRole']): void | Promise<void>;

  /**
   * First-contact capture: set the sender's role and, optionally, tag them on
   * a property (`propertyTag`). The tag is what the backend identity resolver
   * reads to auto-resolve future inbound from this address — so a one-time
   * "associate this sender" decision trains the matcher. One round-trip to
   * `updateCockpitContact`.
   */
  captureSenderAssociation(
    contactId: string,
    input: {
      role?: ContactRole;
      propertyTag?: { propertyId: string; role: ContactRole; roleSubtype?: string };
    }
  ): void | Promise<void>;
}

export const COCKPIT_STORE = new InjectionToken<ICockpitStore>('COCKPIT_STORE');

/**
 * True when `COCKPIT_STORE` is the in-memory demo store (#270). Resolved ONCE
 * per cockpit route activation by `provideCockpitStore()`, so the screen can say
 * out loud that its cards are fixtures — an unannounced demo store reads as real
 * data, which is how a fresh workspace came up full of somebody else's tickets.
 */
export const COCKPIT_DEMO_MODE = new InjectionToken<boolean>('COCKPIT_DEMO_MODE');
